import {
  mapArtistType,
  mapCompanyContactType,
  useFindArtistSuspense,
  useFindCompanyContactsSuspense,
} from '@api';
import {
  EditActionIcon,
  TouringCard,
  TouringCardActions,
  TouringCardHeader,
  TouringCardSection,
  TouringCardTitle,
} from '@common';
import { usePermissionContext } from '@hypeit-gmbh/hypeit-ui-core';
import { Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import type { GetCompanyContactsResponseType } from 'src/api/generated/touring/types';
import { Route } from 'src/routes/_rootLayout/artists/$id.general';
import {
  ArtistContactsFormSchemaType,
  ArtistContactsTable,
  EditArtistContactsForm,
} from './components';
import { useUpdateArtistContacts } from './hooks';

export const ArtistContacts: React.FC = () => {
  const { t } = useTranslation('artistDetailsGeneral');
  const { id } = Route.useParams();
  const [opened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const { submitUpdateArtistContacts } = useUpdateArtistContacts(id);
  const { data: artist } = useFindArtistSuspense(id, {
    query: { select: (data) => mapArtistType(data.data) },
  });
  const contactsSelect = {
    select: (data: GetCompanyContactsResponseType) =>
      data.data.map(mapCompanyContactType),
  };
  const { data: agents } = useFindCompanyContactsSuspense(
    { module: 'artists', scope: 'agent' },
    { query: contactsSelect }
  );
  const { data: buyers } = useFindCompanyContactsSuspense(
    { module: 'artists', scope: 'buyer' },
    { query: contactsSelect }
  );
  const { data: managers } = useFindCompanyContactsSuspense(
    { module: 'artists', scope: 'manager' },
    { query: contactsSelect }
  );
  const { data: productManagers } = useFindCompanyContactsSuspense(
    { module: 'artists', scope: 'product-manager' },
    { query: contactsSelect }
  );
  const { data: projectLeads } = useFindCompanyContactsSuspense(
    { module: 'artists', scope: 'project-lead' },
    { query: contactsSelect }
  );
  const { data: coordinators } = useFindCompanyContactsSuspense(
    { module: 'artists', scope: 'coordinator' },
    { query: contactsSelect }
  );
  const { hasPermission } = usePermissionContext();

  const userCanEditArtist = hasPermission({
    permission: 'ARTISTS_GENERAL',
    action: 'WRITE',
  });

  const handleSubmit = async (data: ArtistContactsFormSchemaType) => {
    const success = await submitUpdateArtistContacts(data);
    if (success) {
      closeModal();
    }
  };

  return (
    <>
      <TouringCard data-testid="artist-contacts-card">
        <TouringCardHeader>
          <TouringCardTitle>{t('CONTACTS.TITLE')}</TouringCardTitle>
          <TouringCardActions>
            {userCanEditArtist && <EditActionIcon onClick={openModal} />}
          </TouringCardActions>
        </TouringCardHeader>
        <TouringCardSection>
          <ArtistContactsTable artist={artist} />
        </TouringCardSection>
      </TouringCard>
      <Modal
        opened={opened}
        onClose={closeModal}
        size="md"
        title={t('EDIT_CONTACTS_MODAL.TITLE')}
      >
        <EditArtistContactsForm
          artist={artist}
          agents={agents}
          buyers={buyers}
          managers={managers}
          productManagers={productManagers}
          projectLeads={projectLeads}
          coordinators={coordinators}
          onSubmit={handleSubmit}
          onClose={closeModal}
        />
      </Modal>
    </>
  );
};
